// ============================================================================
// Command Ring Buffer — Lock-Free SPSC on SharedArrayBuffer
// ============================================================================
// Producer (Main Thread) pushes commands with Atomics.store on write_head.
// Consumer (Worker/Engine) drains commands with Atomics.store on read_head.
// No locks. No CAS on the hot path. Just atomic load/store.
//
// SAB Layout at baseOffset:
//   +0:  write_head (i32, atomic) — owned by producer
//   +4:  read_head  (i32, atomic) — owned by consumer
//   +8:  capacity   (u32)         — immutable after init
//   +12: [slot 0]   (slotSize bytes)
//   ...
//
// Slot Layout (32 bytes default):
//   +0:  command_type (u16)
//   +2:  flags        (u16)
//   +4:  payload      (28 bytes)
import { RING_HEADER_SIZE, COMMAND_SLOT_SIZE } from '../types.js';
const WH_OFFSET = 0; // write_head
const RH_OFFSET = 4; // read_head
const CAP_OFFSET = 8; // capacity
const SLOTS_START = RING_HEADER_SIZE;
/**
 * RingProducer — Main Thread side.
 * Pushes discrete commands into the ring buffer via Atomics.
 */
export class RingProducer {
    int32;
    view;
    base;
    slotSize;
    capacity;
    _dropped = 0;
    constructor(sab, baseOffset, slotSize = COMMAND_SLOT_SIZE) {
        this.int32 = new Int32Array(sab);
        this.view = new DataView(sab);
        this.base = baseOffset;
        this.slotSize = slotSize;
        this.capacity = this.view.getUint32(baseOffset + CAP_OFFSET, true);
    }
    /**
     * Push a command into the ring buffer.
     *
     * Returns true if the command was enqueued successfully.
     * Returns false if the ring buffer is full. The command is NOT enqueued.
     * The caller must decide how to handle this: retry, drop, or backpressure.
     *
     * SPSC invariant: the producer ONLY writes write_head. It NEVER touches
     * read_head. Violating this invariant is a data race.
     */
    push(commandType, flags = 0, writePayload) {
        const whIdx = (this.base + WH_OFFSET) >> 2;
        const rhIdx = (this.base + RH_OFFSET) >> 2;
        const wh = Atomics.load(this.int32, whIdx);
        const nextWh = (wh + 1) % this.capacity;
        // Check if full — read_head is read-only for the producer
        const rh = Atomics.load(this.int32, rhIdx);
        if (nextWh === rh) {
            // Buffer is full. Do NOT advance read_head (that's the consumer's job).
            // Return false and let the caller decide.
            this._dropped++;
            return false;
        }
        // Write the command into the slot
        const slotOff = this.base + SLOTS_START + wh * this.slotSize;
        this.view.setUint16(slotOff, commandType, true);
        this.view.setUint16(slotOff + 2, flags, true);
        if (writePayload) {
            writePayload(this.view, slotOff + 4);
        }
        else {
            // Zero payload
            for (let i = 4; i < this.slotSize; i += 4) {
                this.view.setUint32(slotOff + i, 0, true);
            }
        }
        // Publish: advance write head (must be AFTER slot is fully written)
        Atomics.store(this.int32, whIdx, nextWh);
        return true;
    }
    /** Push with no payload */
    pushSimple(commandType) {
        return this.push(commandType);
    }
    /** Push with a single u32 payload */
    pushU32(commandType, value) {
        return this.push(commandType, 0, (v, off) => v.setUint32(off, value, true));
    }
    /** Push with a u32 pair */
    pushU32Pair(commandType, a, b) {
        return this.push(commandType, 0, (v, off) => {
            v.setUint32(off, a, true);
            v.setUint32(off + 4, b, true);
        });
    }
    /** Push with two floats */
    pushF32Pair(commandType, a, b) {
        return this.push(commandType, 0, (v, off) => {
            v.setFloat32(off, a, true);
            v.setFloat32(off + 4, b, true);
        });
    }
    /** Current number of unread commands */
    get pending() {
        const wh = Atomics.load(this.int32, (this.base + WH_OFFSET) >> 2);
        const rh = Atomics.load(this.int32, (this.base + RH_OFFSET) >> 2);
        return (wh - rh + this.capacity) % this.capacity;
    }
    get droppedCount() { return this._dropped; }
}
/**
 * Lightweight wrapper that provides DataView-like reads at a given byte offset
 * within the underlying SAB. Avoids allocating a new DataView per ring slot.
 *
 * The handler receives this as `payloadView`. Reads are relative to the payload
 * start (offset 0 = first payload byte), matching the old DataView(sab, payloadOff) API.
 *
 * IMPORTANT: The internal base offset changes between drain() iterations.
 * Do NOT store a reference beyond the handler callback scope.
 */
class OffsetDataView {
    view;
    _base = 0;
    constructor(view) {
        this.view = view;
    }
    getUint8(off) { return this.view.getUint8(this._base + off); }
    getInt8(off) { return this.view.getInt8(this._base + off); }
    getUint16(off, le) { return this.view.getUint16(this._base + off, le); }
    getInt16(off, le) { return this.view.getInt16(this._base + off, le); }
    getUint32(off, le) { return this.view.getUint32(this._base + off, le); }
    getInt32(off, le) { return this.view.getInt32(this._base + off, le); }
    getFloat32(off, le) { return this.view.getFloat32(this._base + off, le); }
    getFloat64(off, le) { return this.view.getFloat64(this._base + off, le); }
    get byteLength() { return this.view.byteLength - this._base; }
    get byteOffset() { return this._base; }
    get buffer() { return this.view.buffer; }
}
/**
 * RingConsumer — Worker/Engine side.
 * Drains commands in FIFO order via Atomics.
 */
export class RingConsumer {
    int32;
    view;
    base;
    slotSize;
    capacity;
    /**
     * Zero-allocation payload view. Instead of creating a new DataView per slot,
     * we keep a single OffsetDataView wrapper over the full SAB and update its
     * internal base offset each iteration. This eliminates GC pressure at high
     * command throughput (e.g. continuous mouse move events generating 60+ cmds/s).
     */
    offsetView;
    /**
     * Pre-allocated RingCommand object — reused every drain() iteration.
     * The handler must not retain a reference; all data must be copied before returning.
     */
    reusableCmd;
    constructor(sab, baseOffset, slotSize = COMMAND_SLOT_SIZE) {
        this.int32 = new Int32Array(sab);
        this.view = new DataView(sab);
        this.base = baseOffset;
        this.slotSize = slotSize;
        this.capacity = this.view.getUint32(baseOffset + CAP_OFFSET, true);
        this.offsetView = new OffsetDataView(this.view);
        // Cast: OffsetDataView is a structural subtype of DataView for read operations.
        // Handlers only call get*() methods on the payloadView, which OffsetDataView provides.
        this.reusableCmd = { type: 0, flags: 0, payloadView: this.offsetView };
    }
    /**
     * Drain all pending commands, calling handler for each in FIFO order.
     * Returns number of commands drained.
     *
     * read_head is advanced AFTER each command is successfully handled. If a
     * handler throws, the faulting command is consumed (not re-delivered) and
     * the exception propagates to the caller. Remaining commands stay in the
     * ring and will be drained on the next call. This prevents duplicate
     * execution of already-processed commands (e.g. double undo).
     *
     * IMPORTANT: The `payloadView` in each RingCommand is reused between calls.
     * Do NOT store a reference to it beyond the handler callback scope.
     */
    drain(handler, onCommandFailed) {
        const whIdx = (this.base + WH_OFFSET) >> 2;
        const rhIdx = (this.base + RH_OFFSET) >> 2;
        const wh = Atomics.load(this.int32, whIdx);
        let rh = Atomics.load(this.int32, rhIdx);
        let count = 0;
        const cmd = this.reusableCmd;
        const ov = this.offsetView;
        while (rh !== wh) {
            const slotOff = this.base + SLOTS_START + rh * this.slotSize;
            // Update the shared offset view — zero allocations
            ov._base = slotOff + 4;
            cmd.type = this.view.getUint16(slotOff, true);
            cmd.flags = this.view.getUint16(slotOff + 2, true);
            // Advance read_head BEFORE calling the handler. This ensures the faulting
            // command is consumed even if the handler throws — it won't be re-delivered
            // on the next drain() call.
            count++;
            rh = (rh + 1) % this.capacity;
            Atomics.store(this.int32, rhIdx, rh);
            try {
                handler(cmd);
            }
            catch (err) {
                if (onCommandFailed) {
                    onCommandFailed(cmd.type, err instanceof Error ? err : new Error(String(err)));
                }
                else {
                    throw err; // preserve existing behavior when no callback provided
                }
            }
        }
        return count;
    }
    /** Number of commands available */
    get available() {
        const wh = Atomics.load(this.int32, (this.base + WH_OFFSET) >> 2);
        const rh = Atomics.load(this.int32, (this.base + RH_OFFSET) >> 2);
        return (wh - rh + this.capacity) % this.capacity;
    }
}
//# sourceMappingURL=ring-buffer.js.map